import { EmptyState, Link, t } from '@kanbios/ui';
import { useNavigate } from 'react-router-dom';
import type { maintenance } from '@kanbios/api-client';
import { EnvironmentBadges } from '../../components/Inventory';
import { IndexList, stateColumn, ViewEmpty } from '../../components/IndexList';
import { useSession } from '../../lib/session';

type Row = maintenance.components['schemas']['ApplicationRow'];

/**
 * Applications (SPEC-005): a row opens the application page (SPEC-033, D51; no longer
 * its project page, ADR-011). « Supervision » opens its Supervision tab (SPEC-008).
 */
export function ApplicationsPage() {
  const { enums } = useSession();
  const navigate = useNavigate();
  const role = (value: string) => enums.applicationRoles.find(r => r.value === value)?.label ?? value;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  return (
    <IndexList<Row>
      id="applications"
      title={t('applications.title')}
      subtitle={() => t('applications.subtitle')}
      search={t('applications.search')}
      rowHref={r => `/applications/${encodeURIComponent(r.name)}`}
      columns={[
        stateColumn<Row>(enums),
        { key: 'name', label: t('applications.columns.name'), mono: true, pinned: 'left', width: 240, minWidth: 160, sortable: true },
        { key: 'client', label: t('applications.columns.client'), width: 200, grow: true, sortable: true, render: r => `${r.clientTitle} › ${r.projectTitle}` },
        { key: 'role', label: t('applications.columns.role'), width: 140, sortable: true, render: r => role(r.role) },
        { key: 'environments', label: t('applications.columns.environments'), width: 360, render: r => <EnvironmentBadges environments={r.environments} /> },
        { key: 'prodVersion', label: t('applications.columns.prodVersion'), width: 140, render: r => r.prodVersion ?? t('inventory.versionMissing') },
        { key: 'team', label: t('applications.columns.team'), width: 150, sortable: true },
        {
          key: 'monitoring', label: t('applications.columns.monitoring'), width: 130, sortable: false,
          render: r => {
            const href = `/applications/${encodeURIComponent(r.name)}?tab=monitoring`;
            // The link, not the row: the row opens the application's overview.
            return <Link href={href} onClick={(e: { preventDefault(): void; stopPropagation(): void }) => { e.preventDefault(); e.stopPropagation(); navigate(href); }}>{t('applications.monitoring')}</Link>;
          },
        },
      ]}
      facets={[
        { key: 'state', label: t('inventory.filters.state') },
        { key: 'client', label: t('inventory.filters.client') },
        { key: 'project', label: t('inventory.filters.project') },
        { key: 'role', label: t('inventory.filters.role'), valueLabel: role },
        { key: 'environment', label: t('inventory.filters.environment'), valueLabel: v => label(enums.environments, v) },
        { key: 'team', label: t('inventory.filters.team') },
        { key: 'criticality', label: t('inventory.filters.criticality'), valueLabel: v => label(enums.criticalities, v) },
      ]}
      emptyFor={(view, setView, data) =>
        data && data.outOf === 0
          ? <EmptyState framed={false} icon="apps" title={t('applications.empty.catalogue')} />
          : view !== 'all'
            ? <ViewEmpty list="applications" view={view} icon="apps" onSeeAll={() => setView('all')} />
            : <EmptyState framed={false} icon="apps" title={t('inventory.noResult')} />}
    />
  );
}
